import React, { useState } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { ChevronDownIcon, BuildingOfficeIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';

const BranchSwitcher: React.FC = () => {
  const { 
    branches, 
    currentBranch, 
    switchBranch, 
    canViewAllBranches, 
    isViewingAllBranches,
    loading 
  } = useBranch();
  const [isOpen, setIsOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2">
        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 animate-pulse" />
        <span className="text-sm text-gray-500">Loading branches...</span>
      </div>
    );
  }

  if (branches.length === 0) {
    return null;
  }

  // If user has only one branch and can't view all, don't show switcher
  if (branches.length === 1 && !canViewAllBranches) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2">
        <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">{branches[0].name}</span>
      </div>
    );
  }

  const displayName = isViewingAllBranches ? 'All Branches' : currentBranch?.name || 'Select Branch';

  return (
    <div className="relative">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center space-x-2">
          {isViewingAllBranches ? (
            <GlobeAltIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
          )}
          <span>{displayName}</span>
        </div>
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="py-1" role="menu">
              {canViewAllBranches && (
                <>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                      isViewingAllBranches ? 'bg-gray-50 text-indigo-600' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      switchBranch('all');
                      setIsOpen(false);
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <GlobeAltIcon className="h-5 w-5" />
                      <span>All Branches</span>
                    </div>
                    {isViewingAllBranches && <CheckIcon className="h-4 w-4" />}
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                </>
              )}
              
              {branches.map((branch) => (
                <button
                  key={branch.id}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                    currentBranch?.id === branch.id && !isViewingAllBranches 
                      ? 'bg-gray-50 text-indigo-600' 
                      : 'text-gray-700'
                  }`}
                  onClick={() => {
                    switchBranch(branch.id);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2">
                      <BuildingOfficeIcon className="h-5 w-5" />
                      <span className="font-medium">{branch.name}</span>
                      {branch.isHeadOffice && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">HQ</span>
                      )}
                    </div>
                    {branch.code && (
                      <span className="text-xs text-gray-500 ml-7">{branch.code}</span>
                    )}
                    {branch.userPermissions?.isPrimaryBranch && (
                      <span className="text-xs text-green-600 ml-7">Primary Branch</span>
                    )}
                  </div>
                  {currentBranch?.id === branch.id && !isViewingAllBranches && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0" />
                  )}
                </button>
              ))}
              
              {branches.length === 0 && !canViewAllBranches && (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No branches available
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BranchSwitcher;
